// @ts-expect-error - toastify-js types not available
import Toastify from 'toastify-js'
import 'toastify-js/src/toastify.css'
import { NAVBAR_YELLOW, NAVBAR_YELLOW_DARK } from '@/config/theme-colors'

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface NotificationOptions {
  duration?: number
  position?: 'top' | 'bottom'
  gravity?: 'top' | 'bottom'
  close?: boolean
}

/**
 * Professional notification service using Toastify
 * Provides consistent, compact, and professional notifications across the application
 */
export class NotificationService {
  private static instance: NotificationService

  private constructor() {}

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService()
    }
    return NotificationService.instance
  }

  /**
   * Show a notification toast
   * @param message - The message to display
   * @param type - The type of notification (success, error, warning, info)
   * @param options - Additional options for the notification
   */
  public showToast(message: string, type: NotificationType = 'info', options: NotificationOptions = {}): void {
    // Define icons for each type
    const icons = {
      success: '✓',
      error: '✕',
      warning: '⚠',
      info: 'ℹ',
    }

    // Define colors for each type (warning uses header/navbar brand color)
    const colors = {
      success: {
        background: '#10b981',
        border: '#059669',
        icon: '#ffffff',
        text: '#ffffff',
      },
      error: {
        background: '#ef4444',
        border: '#dc2626',
        icon: '#ffffff',
        text: '#ffffff',
      },
      warning: {
        background: NAVBAR_YELLOW,
        border: NAVBAR_YELLOW_DARK,
        icon: '#000000',
        text: '#000000',
      },
      info: {
        background: '#0f172a',
        border: '#020617',
        icon: '#ffffff',
        text: '#ffffff',
      },
    }

    const config = {
      text: message,
      duration: options.duration || 4000,
      gravity: options.gravity || 'top',
      position: options.position || 'right',
      stopOnFocus: true,
      close: options.close !== false,
      style: {
        background: colors[type].background,
        color: colors[type].text,
        borderRadius: '6px',
        padding: '12px 16px',
        fontSize: '13px',
        fontWeight: '500',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: `1px solid ${colors[type].border}`,
        minWidth: '280px',
        maxWidth: '400px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        lineHeight: '1.4',
      },
      onClick: function (this: any) {
        // Close on click
        this.hideToast()
      },
    }

    // Create custom HTML with icon
    const toastElement = document.createElement('div')
    toastElement.style.display = 'flex'
    toastElement.style.alignItems = 'center'
    toastElement.style.gap = '8px'

    const iconSpan = document.createElement('span')
    iconSpan.textContent = icons[type]
    iconSpan.style.fontSize = '14px'
    iconSpan.style.fontWeight = 'bold'
    iconSpan.style.color = colors[type].icon
    iconSpan.style.minWidth = '16px'

    const messageSpan = document.createElement('span')
    messageSpan.textContent = message
    messageSpan.style.flex = '1'
    messageSpan.style.color = colors[type].text

    toastElement.appendChild(iconSpan)
    toastElement.appendChild(messageSpan)
    ;(config as any).node = toastElement

    Toastify(config).showToast()
  }

  /**
   * Show a success notification
   */
  public success(message: string, options?: NotificationOptions): void {
    this.showToast(message, 'success', options)
  }

  /**
   * Show an error notification
   */
  public error(message: string, options?: NotificationOptions): void {
    this.showToast(message, 'error', options)
  }

  /**
   * Show a warning notification
   */
  public warning(message: string, options?: NotificationOptions): void {
    this.showToast(message, 'warning', options)
  }

  /**
   * Show an info notification
   */
  public info(message: string, options?: NotificationOptions): void {
    this.showToast(message, 'info', options)
  }

  /**
   * Show a loading notification (info type with longer duration)
   */
  public loading(message: string): void {
    this.showToast(message, 'info', { duration: 6000 })
  }

  // ===== CRUD NOTIFICATIONS =====

  // User CRUD Notifications
  public showUserCreateSuccess(user: any): void {
    const roleText =
      user.roleIds && user.roleIds.length > 0
        ? `Role: ${user.roleIds.join(', ')}`
        : user.roleNames && user.roleNames.length > 0
          ? `Role: ${user.roleNames.join(', ')}`
          : 'No roles assigned'

    const message = `✅ User created successfully!\n\n👤 ${user.firstName} ${user.lastName}\n📧 ${user.email}\n👨‍💼 ${
      user.userName || 'Not set'
    }\n🔐 ${roleText}\n📊 Status: ${user.isActive ? 'Active' : 'Inactive'}`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showUserUpdateSuccess(user: any): void {
    const roleText =
      user.roleIds && user.roleIds.length > 0
        ? `Role: ${user.roleIds.join(', ')}`
        : user.roleNames && user.roleNames.length > 0
          ? `Role: ${user.roleNames.join(', ')}`
          : 'No roles assigned'

    const message = `✅ User updated successfully!\n\n👤 ${user.firstName} ${user.lastName}\n📧 ${user.email}\n👨‍💼 ${
      user.userName || 'Not set'
    }\n🔐 ${roleText}\n📊 Status: ${user.isActive ? 'Active' : 'Inactive'}`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showUserDeleteSuccess(user: any): void {
    const message = `✅ User deleted successfully!\n\n🗑️ Deleted User:\n👤 ${user.firstName} ${user.lastName}\n📧 ${user.email}\n⚠️ This action cannot be undone`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showUserResetPasswordSuccess(user: any, newPassword: string): void {
    const message = `✅ Password reset successfully!\n\n👤 ${user.firstName} ${user.lastName}\n📧 ${user.email}\n🔐 New Password: ${newPassword}\n⚠️ Please share this password securely with the user`

    this.showToast(message, 'success', { duration: 6000 })
  }

  public showUserError(operation: string, error: any): void {
    const err = error as any
    let message = `Failed to ${operation} user`

    if (err?.response?.data) {
      const errorData = err.response.data
      if (errorData.details) {
        message = `❌ ${errorData.message || `Failed to ${operation} user`}\n\n${errorData.details}`
      } else if (errorData.errors && Array.isArray(errorData.errors)) {
        message = `❌ ${errorData.message || `Failed to ${operation} user`}\n\n${errorData.errors.join('\n')}`
      } else {
        message = errorData.message || errorData
      }
    } else {
      message = err?.message || `Failed to ${operation} user`
    }

    this.showToast(message, 'error', { duration: 6000 })
  }

  // Role CRUD Notifications
  public showRoleCreateSuccess(role: any): void {
    const message = `✅ Role created successfully!\n\n🔐 ${role.name}\n📝 ${
      role.description || 'No description'
    }\n📊 Status: ${role.isActive ? 'Active' : 'Inactive'}`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showRoleUpdateSuccess(role: any): void {
    const message = `✅ Role updated successfully!\n\n🔐 ${role.name}\n📝 ${
      role.description || 'No description'
    }\n📊 Status: ${role.isActive ? 'Active' : 'Inactive'}`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showRoleDeleteSuccess(role: any): void {
    const message = `✅ Role deleted successfully!\n\n🗑️ Deleted Role:\n🔐 ${role.name}\n📝 ${
      role.description || 'No description'
    }\n⚠️ This action cannot be undone`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showRoleError(operation: string, error: any): void {
    const err = error as any
    const message = err?.response?.data?.message || err?.message || `Failed to ${operation} role`

    this.showToast(`❌ ${message}`, 'error', { duration: 5000 })
  }

  // Permission CRUD Notifications
  public showPermissionCreateSuccess(permission: any): void {
    const message = `✅ Permission created successfully!\n\n🔐 ${permission.name}\n📝 ${
      permission.description || 'No description'
    }\n🏷️ Code: ${permission.code}\n🔧 Service: ${permission.service || 'General'}`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showPermissionUpdateSuccess(permission: any): void {
    const message = `✅ Permission updated successfully!\n\n🔐 ${permission.name}\n📝 ${
      permission.description || 'No description'
    }\n🏷️ Code: ${permission.code}\n🔧 Service: ${permission.service || 'General'}`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showPermissionDeleteSuccess(permission: any): void {
    const message = `✅ Permission deleted successfully!\n\n🗑️ Deleted Permission:\n🔐 ${permission.name}\n📝 ${
      permission.description || 'No description'
    }\n🏷️ Code: ${permission.code}\n⚠️ This action cannot be undone`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showPermissionError(operation: string, error: any): void {
    const err = error as any
    const message = err?.response?.data?.message || err?.message || `Failed to ${operation} permission`

    this.showToast(`❌ ${message}`, 'error', { duration: 5000 })
  }

  // UserGroup CRUD Notifications
  public showUserGroupCreateSuccess(group: any): void {
    const message = `✅ Group created successfully!\n\n👥 ${group.name}\n📝 ${
      group.description || 'No description'
    }\n📊 Status: ${group.isActive ? 'Active' : 'Inactive'}`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showUserGroupUpdateSuccess(group: any): void {
    const message = `✅ Group updated successfully!\n\n👥 ${group.name}\n📝 ${
      group.description || 'No description'
    }\n📊 Status: ${group.isActive ? 'Active' : 'Inactive'}`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showUserGroupDeleteSuccess(group: any): void {
    const message = `✅ Group deleted successfully!\n\n🗑️ Deleted Group:\n👥 ${group.name}\n📝 ${
      group.description || 'No description'
    }\n⚠️ This action cannot be undone`

    this.showToast(message, 'success', { duration: 5000 })
  }

  public showUserGroupError(operation: string, error: any): void {
    const err = error as any
    const message = err?.response?.data?.message || err?.message || `Failed to ${operation} group`

    this.showToast(`❌ ${message}`, 'error', { duration: 5000 })
  }

  // Bulk Operations
  public showBulkDeleteSuccess(entityType: string, count: number): void {
    this.showToast(`✅ ${count} ${entityType} deleted successfully!`, 'success', { duration: 4000 })
  }

  public showBulkActivateSuccess(entityType: string, count: number): void {
    this.showToast(`✅ ${count} ${entityType} activated successfully!`, 'success', { duration: 4000 })
  }

  public showBulkDeactivateSuccess(entityType: string, count: number): void {
    this.showToast(`✅ ${count} ${entityType} deactivated successfully!`, 'success', { duration: 4000 })
  }

  // Generic CRUD notifications
  public showValidationError(message: string = 'Please fix the validation errors'): void {
    this.showToast(message, 'warning', { duration: 4000 })
  }

  public showFormResetSuccess(message: string = 'Form reset to original values'): void {
    this.showToast(message, 'info', { duration: 3000 })
  }

  public showGenericError(operation: string, entityType: string, error: any): void {
    const err = error as any
    const message = err?.response?.data?.message || err?.message || `Failed to ${operation} ${entityType}`

    this.showToast(`❌ ${message}`, 'error', { duration: 5000 })
  }

  public showNetworkError(): void {
    this.showToast('Network error. Please check your connection.', 'error', { duration: 5000 })
  }

  public showServerError(): void {
    this.showToast('Server error. Please try again later.', 'error', { duration: 5000 })
  }
}

// Export singleton instance
export const notificationService = NotificationService.getInstance()

// Export convenience functions
export const showToast = (message: string, type: NotificationType = 'info', options?: NotificationOptions) =>
  notificationService.showToast(message, type, options)

export const showSuccess = (message: string, options?: NotificationOptions) =>
  notificationService.success(message, options)

export const showError = (message: string, options?: NotificationOptions) => notificationService.error(message, options)

export const showWarning = (message: string, options?: NotificationOptions) =>
  notificationService.warning(message, options)

export const showInfo = (message: string, options?: NotificationOptions) => notificationService.info(message, options)

export const showLoading = (message: string) => notificationService.loading(message)

// CRUD notification helpers
export const showUserCreateSuccess = (user: any) => notificationService.showUserCreateSuccess(user)
export const showUserUpdateSuccess = (user: any) => notificationService.showUserUpdateSuccess(user)
export const showUserDeleteSuccess = (user: any) => notificationService.showUserDeleteSuccess(user)
export const showUserResetPasswordSuccess = (user: any, newPassword: string) =>
  notificationService.showUserResetPasswordSuccess(user, newPassword)
export const showUserError = (operation: string, error: any) => notificationService.showUserError(operation, error)

export const showRoleCreateSuccess = (role: any) => notificationService.showRoleCreateSuccess(role)
export const showRoleUpdateSuccess = (role: any) => notificationService.showRoleUpdateSuccess(role)
export const showRoleDeleteSuccess = (role: any) => notificationService.showRoleDeleteSuccess(role)
export const showRoleError = (operation: string, error: any) => notificationService.showRoleError(operation, error)

export const showPermissionCreateSuccess = (permission: any) =>
  notificationService.showPermissionCreateSuccess(permission)
export const showPermissionUpdateSuccess = (permission: any) =>
  notificationService.showPermissionUpdateSuccess(permission)
export const showPermissionDeleteSuccess = (permission: any) =>
  notificationService.showPermissionDeleteSuccess(permission)
export const showPermissionError = (operation: string, error: any) =>
  notificationService.showPermissionError(operation, error)

export const showUserGroupCreateSuccess = (group: any) => notificationService.showUserGroupCreateSuccess(group)
export const showUserGroupUpdateSuccess = (group: any) => notificationService.showUserGroupUpdateSuccess(group)
export const showUserGroupDeleteSuccess = (group: any) => notificationService.showUserGroupDeleteSuccess(group)
export const showUserGroupError = (operation: string, error: any) =>
  notificationService.showUserGroupError(operation, error)

export const showBulkDeleteSuccess = (entityType: string, count: number) =>
  notificationService.showBulkDeleteSuccess(entityType, count)
export const showBulkActivateSuccess = (entityType: string, count: number) =>
  notificationService.showBulkActivateSuccess(entityType, count)
export const showBulkDeactivateSuccess = (entityType: string, count: number) =>
  notificationService.showBulkDeactivateSuccess(entityType, count)

export const showValidationError = (message?: string) => notificationService.showValidationError(message)
export const showFormResetSuccess = (message?: string) => notificationService.showFormResetSuccess(message)
export const showGenericError = (operation: string, entityType: string, error: any) =>
  notificationService.showGenericError(operation, entityType, error)
export const showNetworkError = () => notificationService.showNetworkError()
export const showServerError = () => notificationService.showServerError()
